import React from 'react';
import { T } from '@transifex/react';

import type { Capability } from 'src/app/capabilities';

import { RoutingMap } from '../../../routing/utils';

export type NavDataOptions = {
  accountEnabled?: boolean;
  // "Beskeder" hub — cases (Sager) and conversations live under one nav item.
  // Enabled when either source is on; cases are ungated, so today this is
  // always true (see PortalSidebar and inbox/flags.ts).
  beskederEnabled?: boolean;
  reportsEnabled?: boolean;
  /** `crm_enable_composting` — the Self Composting service rollout flag
   *  (staff-set on the backend; the composting API also 404s while it is
   *  off). Defaults false: absence of a flag is never "enabled". */
  compostingEnabled?: boolean;
};

// Nav item shape. Icons are NOT declared here — the live renderer
// (`src/layouts/PortalSidebar.tsx`) maps `key` → an Untitled UI icon via its
// `NAV_ICONS` table. Order below is the on-screen order.
//
// An optional `capability` tags a gate-able item: the renderer drops it unless
// the active property grants that capability (via useCapabilities().can).
// Company feature flags still gate whether the item is spread in at all — both
// axes must pass. Core items (Overview/Emptyings/Agreements/Services) carry no
// capability and are never gated.
export const createNavData: (
  map: RoutingMap,
  options?: NavDataOptions
) => any[] = (
  routingMap,
  {
    accountEnabled = false,
    beskederEnabled = false,
    reportsEnabled = false,
    compostingEnabled = false,
  } = {}
) => [
  {
    key: 'self-service',
    title: <T _str="Overview" />,
    link: routingMap.app.selfService._,
  },
  {
    key: 'collections',
    title: <T _str="Emptyings" />,
    link: routingMap.app.collections._,
  },
  {
    // "Agreements" is today's Containers (beholdere) page, relabeled. Always
    // on, like Containers was — it points at the existing /containers route.
    // Shared pools live here too, as a section on the page (crm_enable_pools
    // gates the section, not a nav item).
    key: 'agreements',
    title: <T _str="Agreements" />,
    link: routingMap.app.containers._,
  },
  {
    // "Services" lists the property's ticket types as order/request cards.
    // Always on — backed by the real GET property/{id} ticket_types.
    key: 'services',
    title: <T _str="Services" />,
    link: routingMap.app.services._,
  },
  ...(beskederEnabled
    ? [
        {
          key: 'messages',
          title: <T _str="Messages" />,
          link: routingMap.app.messages._,
        },
      ]
    : []),
  ...(reportsEnabled
    ? [
        {
          key: 'reports',
          title: <T _str="Reports" />,
          link: routingMap.app.reports._,
          capability: 'view_reports' satisfies Capability,
        },
      ]
    : []),
  ...(accountEnabled
    ? [
        {
          key: 'account',
          title: <T _str="My account" />,
          link: routingMap.app.account._,
        },
      ]
    : []),
  ...(compostingEnabled
    ? [
        {
          key: 'composting',
          title: <T _str="Self Composting service" />,
          link: routingMap.app.composting._,
        },
      ]
    : []),
];
